import {
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
    Typography,
    Input
} from "@material-tailwind/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import ShowLoader from "@/components/Loader";
import Toaster from "@/components/Toaster";
import { fetchCountry, fetchCity } from "@/store/slices/internalUsersSlice";
import { fetchDeliveryPartners, assignOrderToDeliveryPartner, fetchOrderPayments, assignMultipleOrderItemToDeliveryPartner, fetchOrderItems } from "@/store/slices/orderSlice";
import DeliveryLocationSearch from "@/components/DeliveryLocationSearch";
import { useTranslation } from "react-i18next";

const customSelectStyles = {
    control: (provided, state) => ({
        ...provided,
        borderRadius: "8px",
        minHeight: "50px", // Taller fields like reference image
        borderColor: state.isFocused ? "#212121" : "#CFD2D9",
        boxShadow: state.isFocused ? "0 0 0 1px #212121" : "none",
        "&:hover": { borderColor: "#CFD2D9" },
    }),
    singleValue: (provided) => ({ ...provided, color: "#111827", fontSize: "14px" }),
    placeholder: (provided) => ({ ...provided, color: "#9CA3AF", fontSize: "14px" }),
    menu: (provided) => ({ ...provided, zIndex: 50 }),
};

const priorities = [
    { label: "High", value: "high" },
    { label: "Medium", value: "medium" },
    { label: "Low", value: "low" },
];

const maxTenDigits = (val) => !val || String(val).length <= 10;

const schema = (order_pending_amount, t) =>

    yup.object().shape({
        country_id: yup.string().required(t('country_is_required')),
        city_id: yup.string().required(t('city_is_required')),
        delivery_agent_id: yup.string().required(t('delivery_agent_required')),
        deadline: yup.string().required(t('deadline_date_required')),
        priority: yup.string().required(t('priority_required')),
        delivery_address: yup.string().trim().required(t('delivery_location_is_required'))
            .min(3, t('delivery_location_min_length'))
            .max(150, t('delivery_location_max_length')),
        delivery_landmark: yup
            .string()
            .trim()
            .required(t('landmark_is_required')),
        latitude: yup
            .mixed()
            .test(
                "has-latitude",
                t('select_location_for_coordinates'),
                (v) => v !== null && v !== undefined && v !== "" && !Number.isNaN(Number(v))
            ),
        longitude: yup
            .mixed()
            .test(
                "has-longitude",
                t('select_location_for_coordinates'),
                (v) => v !== null && v !== undefined && v !== "" && !Number.isNaN(Number(v))
            ),
        notes: yup.string().required(t('note_is_required')).max(150, t('max_150_characters')),
        full_name: yup.string().trim().required(t('recipient_name_is_required')).max(100, t('max_100_characters')),
        country_code: yup.string().trim().required(t('country_code_is_required')),
        mobile_number: yup
            .string()
            .trim()
            .required(t('mobile_number_is_required'))
            .test("max-10-digits", t('mobile_number_max_digits'), maxTenDigits)
            .matches(/^\d+$/, t('mobile_number_digits_only')),
        delivery_charge: yup
            .number()
            .typeError(t('delivery_charge_must_be_a_number'))
            .required(t('delivery_charge_is_required'))
            .min(0, t('delivery_charge_cannot_be_negative')),

        cash_amount: yup
            .number()
            .typeError(t('amount_must_be_a_number'))
            .required(t('amount_to_be_collected_is_required'))
            .min(0, t('cannot_be_negative'))
            .max(order_pending_amount, t('max_amount', { amount: order_pending_amount })),
    });

export default function AssignMultipleItemToDeliveryAgentModal({ onClose, rfqProductIds, order }) {
    const dispatch = useDispatch();
    const { t } = useTranslation('order');
    const { t: ct } = useTranslation('common');


    const countries = useSelector((state) => state.internalUser?.countries || []);
    const cities = useSelector((state) => state.internalUser?.cities || []);
    const { delivery_partners = [], loading: orderLoading } = useSelector((state) => state.order);
    const { loading: userLoading } = useSelector((state) => state.internalUsers || {});

    const isLoading = orderLoading || userLoading;

    const {
        register,
        handleSubmit,
        setValue,
        watch,
        control,
        reset,
        formState: { errors },
    } = useForm({
        defaultValues: {
            country_id: null,
            city_id: null,
            delivery_agent_id: null,
            delivery_charge: null,
            cash_amount: null,
            deadline: null,
            priority: "medium",
            delivery_address: "",
            delivery_landmark: "",
            latitude: null,
            longitude: null,
            notes: null,
            full_name: "",
            country_code: "+966",
            mobile_number: "",
        },
        resolver: yupResolver(schema(order?.payment_summary?.due_amount, t)),
    });

    useEffect(() => {
        dispatch(fetchCountry());
    }, [dispatch]);

    const onSubmit = async (formData) => {
        try {
            dispatch(assignMultipleOrderItemToDeliveryPartner({ rfq_product_ids: rfqProductIds, order_id: order?.id, ...formData })).unwrap().then(res => {
                if (res?.status) {
                    Toaster.success(t('delivery_agents_assigned_success'));
                    onClose();
                    dispatch(fetchOrderItems({ rfq_id: order?.rfq_id, search: '' }));
                } else {
                    Toaster.error(res?.message || t('assignment_failed_try_again'));
                }
            });
        } catch (err) { Toaster.error(err?.message || "Assignment failed"); }
    };

    const inputLabelClass = "mb-2 block text-sm font-semibold text-[#1F2937]";
    const commonInputClass = "!border !border-[#CFD2D9] rounded-lg h-[50px] !text-[#111827] focus:!border-[#212121] transition";

    const now = new Date().toISOString().slice(0, 16);

    const formValues = watch();
    const [showInfo, setShowInfo] = useState({ name: true, phone: true, email: true });

    // Auto-fill customer delivery address when opening assign modal
    useEffect(() => {
        const customerAddress = order?.rfq?.customer_delivery_address || order?.delivery_address;
        const latitude = order?.rfq?.latitude || order?.latitude;
        const longitude = order?.rfq?.longitude || order?.longitude;

        if (customerAddress) {
            setValue("delivery_address", customerAddress, {
                shouldValidate: true,
            });

            // API does not provide a separate landmark,
            // so use the customer delivery address as the initial landmark.
            setValue("delivery_landmark", customerAddress, {
                shouldValidate: true,
            });
        }

        if (latitude !== null && latitude !== undefined && latitude !== "") {
            setValue("latitude", Number(latitude), {
                shouldValidate: true,
            });
        }

        if (longitude !== null && longitude !== undefined && longitude !== "") {
            setValue("longitude", Number(longitude), {
                shouldValidate: true,
            });
        }
    }, [order, setValue]);


    return (
        <Dialog open={true} handler={onClose} size="sm" className="rounded-2xl p-0 overflow-hidden shadow-2xl 2xl:min-w-[40%] 2xl:max-w-[40%]">
            {isLoading && <ShowLoader />}
            <DialogHeader className="flex justify-between items-center border-b px-8 py-5 bg-white z-10 relative">
                <Typography className="text-xl font-bold text-gray-900">
                    {t('assign_delivery_agent_title')}
                </Typography>
                <XMarkIcon className="h-6 w-6 cursor-pointer text-gray-500 hover:text-black transition" onClick={onClose} />
            </DialogHeader>

            <form onSubmit={handleSubmit(onSubmit)}>
                {/* This invisible input will take the initial focus instead of the deadline field */}
                <input type="text" style={{ opacity: 0, height: 0, width: 0, position: 'absolute' }} autoFocus />
                <DialogBody className="p-0 max-h-[70vh] overflow-y-auto">
                    {/* Padding added here to matches the design spacing */}
                    <div className="px-8 pt-6 pb-2 space-y-6">

                        {/* Location Selection Block - No grid, stacked */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="relative">
                                <label className={inputLabelClass}>{t('select_country_label')}</label>
                                <Controller
                                    control={control}
                                    name="country_id"
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            placeholder={t('country_placeholder')}
                                            options={countries}
                                            value={countries.find(c => String(c.value) === String(field.value)) || null}
                                            onChange={(val) => {
                                                field.onChange(val?.value != null ? String(val.value) : "");
                                                if (val?.value) {
                                                    dispatch(fetchCity(val.value));
                                                }
                                            }}
                                            styles={customSelectStyles}
                                        />
                                    )}
                                />
                                {errors.country_id && <p className="text-red-600 text-xs mt-1.5">{errors.country_id.message}</p>}
                            </div>
                            <div className="relative">
                                <label className={inputLabelClass}>{t('select_city_label')}</label>
                                <Controller
                                    control={control}
                                    name="city_id"
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            placeholder={t('city_placeholder')}
                                            options={cities}
                                            value={cities.find(c => String(c.value) === String(field.value)) || null}
                                            onChange={(val) => {
                                                field.onChange(val?.value != null ? String(val.value) : "");
                                                dispatch(fetchDeliveryPartners({ country_id: watch('country_id'), city_id: val?.value }));
                                            }}
                                            styles={customSelectStyles}
                                        />
                                    )}
                                />
                                {errors.city_id && <p className="text-red-600 text-xs mt-1.5">{errors.city_id.message}</p>}
                            </div>
                        </div>

                        {/* Single Agent form (no add-more) */}
                        <div className="space-y-5">
                            <div>
                                <label className={inputLabelClass}>{t('select_delivery_agent_label')}</label>
                                <Controller
                                    control={control}
                                    name="delivery_agent_id"
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            placeholder={t('choose_delivery_agent_placeholder')}
                                            options={delivery_partners}
                                            value={delivery_partners.find(dp => String(dp.value) === String(field.value)) || null}
                                            onChange={(val) => field.onChange(val?.value != null ? String(val.value) : "")}
                                            styles={customSelectStyles}
                                        />
                                    )}
                                />
                                {errors.delivery_agent_id && <p className="text-red-600 text-xs mt-1.5">{errors.delivery_agent_id.message}</p>}
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={inputLabelClass}>{t('deadline_date_label')}</label>
                                    <Input
                                        type="datetime-local"
                                        min={now}
                                        {...register("deadline")}
                                        className={commonInputClass}
                                        containerProps={{
                                            className: "min-w-[100px] h-[50px]",
                                        }}
                                        labelProps={{ className: "hidden" }}

                                    />
                                    {errors.deadline && <p className="text-red-600 text-xs mt-1.5">{errors.deadline.message}</p>}
                                </div>
                                <div>
                                    <label className={inputLabelClass}>{t('priority_label')}</label>
                                    <Controller
                                        control={control}
                                        name="priority"
                                        render={({ field }) => (
                                            <Select
                                                {...field}
                                                options={priorities}
                                                value={priorities.find(p => p.value === field.value)}
                                                onChange={(val) => field.onChange(val?.value)}
                                                styles={customSelectStyles}
                                            />
                                        )}
                                    />
                                    {errors.priority && <p className="text-red-600 text-xs mt-1.5">{errors.priority.message}</p>}
                                </div>
                            </div>

                            <div>
                                <label className={inputLabelClass}>{t('delivery_location_label')}</label>
                                <Controller
                                    control={control}
                                    name="delivery_address"
                                    render={({ field }) => (
                                        <DeliveryLocationSearch
                                            value={field.value}
                                            onChange={({
                                                delivery_address,
                                            }) => {
                                                setValue(
                                                    "delivery_address",
                                                    delivery_address ?? "",
                                                    { shouldValidate: true }
                                                );
                                            }}
                                            placeholder={t('search_area_street_placeholder')}
                                            error={
                                                errors.delivery_address?.message ? errors.delivery_address?.message : ''
                                            }
                                        />
                                    )}
                                />
                            </div>

                            <div>
                                <label className={inputLabelClass}>{t('delivery_landmark_label')}
                                </label>
                                <Controller
                                    control={control}
                                    name="delivery_landmark"
                                    render={({ field }) => (
                                        <DeliveryLocationSearch
                                            value={field.value}
                                            latitude={formValues?.latitude}
                                            longitude={formValues?.longitude}
                                            onChange={({
                                                delivery_landmark,
                                                latitude,
                                                longitude,
                                            }) => {
                                                setValue(
                                                    "delivery_landmark",
                                                    delivery_landmark ?? "",
                                                    { shouldValidate: true }
                                                );
                                                setValue(
                                                    "latitude",
                                                    latitude != null ? Number(latitude) : null,
                                                    { shouldValidate: true }
                                                );
                                                setValue(
                                                    "longitude",
                                                    longitude != null ? Number(longitude) : null,
                                                    { shouldValidate: true }
                                                );
                                            }}
                                            placeholder={t('search_landmark_placeholder')}
                                            error={
                                                errors.delivery_landmark?.message ? errors.delivery_landmark?.message :
                                                    (errors.latitude?.message ||
                                                        errors.longitude?.message) ? 'Please enter valid address' : ''
                                            }
                                        />
                                    )}
                                />
                            </div>
                            <div>
                                <label className={inputLabelClass}>{t('note_label')}</label>
                                <textarea
                                    rows="3"
                                    maxLength={150}
                                    className="w-full border border-[#CFD2D9] rounded-lg px-4 py-3 focus:border-[#212121] outline-none text-sm transition"
                                    {...register("notes")}
                                    placeholder={t('enter_notes_for_agent_placeholder')}
                                />
                                <div className="flex items-center justify-between mt-1">
                                    <span className="text-red-600 text-xs">
                                        {errors.notes?.message || ""}
                                    </span>
                                    <span className="text-xs text-gray-500">
                                        {(formValues?.notes || "").length}/150
                                    </span>
                                </div>
                            </div>
                            <div>
                                <label className={inputLabelClass}>{t('recipient_full_name_label')}</label>
                                <Input
                                    type="text"
                                    {...register("full_name")}
                                    className={commonInputClass}
                                    labelProps={{ className: "hidden" }}
                                    placeholder={t('enter_recipient_full_name_placeholder')}
                                    maxLength={100}
                                />
                                {errors.full_name && (
                                    <p className="text-red-600 text-xs mt-1.5">{errors.full_name.message}</p>
                                )}
                            </div>

                            <div className="flex flex-wrap sm:flex-nowrap gap-4 sm:gap-6 w-full">
                                {/* Country Code Field */}
                                <div className="w-auto shrink-0">
                                    <label className={inputLabelClass}>{t('country_code_label')}</label>
                                    <Input
                                        type="text"
                                        {...register("country_code")}
                                        className={commonInputClass}
                                        labelProps={{ className: "hidden" }}
                                        placeholder="+91"
                                    />
                                    {errors.country_code && (
                                        <p className="text-red-600 text-xs mt-1.5">{errors.country_code.message}</p>
                                    )}
                                </div>

                                {/* Mobile Number Field */}
                                <div className="w-full sm:w-2/3 min-w-[200px]">
                                    <label className={inputLabelClass}>{t('recipient_mobile_number_label')}</label>
                                    <Input
                                        type="text"
                                        {...register("mobile_number")}
                                        className={commonInputClass}
                                        placeholder={t('enter_mobile_number_placeholder')}
                                        labelProps={{ className: "hidden" }}
                                        onInput={(e) => {
                                            e.target.value = e.target.value.replace(/\D/g, "");
                                            if (e.target.value.length > 10) e.target.value = e.target.value.slice(0, 10);
                                        }}
                                    />
                                    {errors.mobile_number && (
                                        <p className="text-red-600 text-xs mt-1.5">{errors.mobile_number.message}</p>
                                    )}
                                </div>
                            </div>
                            <div>
                                <label className={inputLabelClass}>{t('amount_to_be_collected_label')}</label>
                                <Input
                                    type="number"
                                    min={0}
                                    {...register("cash_amount")}
                                    className={commonInputClass}
                                    labelProps={{ className: "hidden" }}
                                    placeholder={t('enter_collected_amount_placeholder')}
                                    onInput={(e) => {
                                        if (e.target.value.length > 10) e.target.value = e.target.value.slice(0, 10);
                                    }}
                                />
                                <div className="flex items-center justify-between mt-1">
                                    <span className="text-red-600 text-xs">
                                        {errors.cash_amount?.message || ""}
                                    </span>
                                    <span className="text-xs text-gray-500">
                                        {String(formValues?.cash_amount ?? "").length}/10
                                    </span>
                                </div>
                            </div>

                            <div>
                                <div className="flex items-start justify-between mb-2 gap-4">
                                    <label className="text-sm font-semibold text-[#1F2937]">
                                        {t('delivery_charge_label')}
                                    </label>

                                    {/* <div className="text-right text-xs text-gray-600 space-y-1">
                                        <p className="font-medium">
                                            {t('product_installation_charge_per_item')}
                                        </p>

                                        {order?.selected_products?.length ? (
                                            order.selected_products.map((product) => (
                                                <p key={product.rfq_product_id}>
                                                    {product.product_name}: {product.installation_charge ?? "0.00"}
                                                </p>
                                            ))
                                        ) : (
                                            <p>{t('no_product_reference_available')}</p>
                                        )}
                                    </div> */}
                                </div>

                                <Input
                                    type="number"
                                    min={0}
                                    {...register("delivery_charge")}
                                    className={commonInputClass}
                                    labelProps={{ className: "hidden" }}
                                    placeholder={t('enter_delivery_charge_placeholder')}
                                    onInput={(e) => {
                                        if (e.target.value.length > 10)
                                            e.target.value = e.target.value.slice(0, 10);
                                    }}
                                />

                                <div className="flex items-center justify-between mt-1">
                                    <span className="text-red-600 text-xs">
                                        {errors.delivery_charge?.message || ""}
                                    </span>

                                    <span className="text-xs text-gray-500">
                                        {String(formValues?.delivery_charge ?? "").length}/10
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Customer Info Section */}
                        <div className="p-1 pb-4">
                            <Typography className="text-sm font-semibold mb-4 text-[#1F2937]">
                                {t('choose_data_to_show_partner')}
                            </Typography>
                            <div className="space-y-3.5 text-sm">
                                {/* Name */}
                                <div className="flex items-center justify-between p-3.5 bg-gray-50 rounded-lg border border-gray-100">
                                    <span className="text-[#374151]">
                                        {t('name_label')}:{" "}
                                        <span className="font-semibold text-[#111827]">
                                            {[order?.customer?.first_name, order?.customer?.last_name]
                                                .filter(Boolean)
                                                .join(" ") || "N/A"}
                                        </span>
                                    </span>
                                    <input
                                        type="checkbox"
                                        className="h-5 w-5 accent-black cursor-pointer rounded"
                                        checked={showInfo.name}
                                        onChange={() => setShowInfo({ ...showInfo, name: !showInfo.name })}
                                    />
                                </div>

                                {/* Phone Number */}
                                <div className="flex items-center justify-between p-3.5 bg-gray-50 rounded-lg border border-gray-100">
                                    <span className="text-[#374151]">
                                        {t('phone_number_label')}:{" "}
                                        <span className="font-semibold text-[#111827]">
                                            {order?.customer?.mobile_number
                                                ? `${order?.customer?.country_code || ""}${order?.customer?.mobile_number}`
                                                : "N/A"}
                                        </span>
                                    </span>
                                    <input
                                        type="checkbox"
                                        className="h-5 w-5 accent-black cursor-pointer rounded"
                                        checked={showInfo.phone}
                                        onChange={() => setShowInfo({ ...showInfo, phone: !showInfo.phone })}
                                    />
                                </div>

                                {/* Email */}
                                <div className="flex items-center justify-between p-3.5 bg-gray-50 rounded-lg border border-gray-100">
                                    <span className="text-[#374151]">
                                        {t('email_label')}:{" "}
                                        <span className="font-semibold text-[#111827]">
                                            {order?.customer?.email || "N/A"}
                                        </span>
                                    </span>
                                    <input
                                        type="checkbox"
                                        className="h-5 w-5 accent-black cursor-pointer rounded"
                                        checked={showInfo.email}
                                        onChange={() => setShowInfo({ ...showInfo, email: !showInfo.email })}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </DialogBody>

                <DialogFooter className="border-t flex justify-end gap-3.5 px-8 py-5 bg-white z-10 relative">
                    <button type="button" onClick={onClose} className="px-6 py-2.5 rounded-lg bg-[#F3F4F6] text-[#374151] font-medium hover:bg-[#E5E7EB] transition-colors">{t('not_now_button')}</button>
                    <button type="submit" className="px-9 py-2.5 rounded-lg bg-[#F2AD25] text-[#111827] font-bold hover:brightness-95 shadow-md transition-all"> {t('assign_now_button')}</button>
                </DialogFooter>
            </form>
        </Dialog>
    );
}